import { useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Grid } from '@mui/material';
import useTheme from 'api/useTheme';
import useUsers from 'api/useUsers';
import { OfferUiProps } from 'common/contracts';
import {
  EAssetType,
  EBundlesInternalViewModel,
  EFeatureFlag
} from 'constants/enums';
import { UploadsTooltips, useUploads } from 'hooks/useUpload';
import { validateLottieFile } from 'utils/lottieAnimationUtil';

import AcCard from 'components/AcCard/AcCard';
import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import AcSelect from 'components/AcSelect/AcSelect';
import { EAcSelectItemRenderType } from 'components/AcSelect/AcSelect.types';

import PopupUiForm from '../Popups';
import { OfferDesignTypes } from '../types';

const OfferUiAssets: React.FC<OfferUiProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  addPictureInputRef,
  getImages,
  setCurrentUploadingField,
  currentUploadingField,
  dup
}) => {
  const [buttonSuffixBadgeType, setButtonSuffixBadgeType] =
    useState<EAcSelectItemRenderType | null>(null);
  const { publisherId } = useParams();
  const { fetchFeatureFlags } = useUsers({ publisherId });
  const { getGeneralTheme: generalTheme } = useTheme(publisherId);
  const hasFeatureFlagDailyBonusBadge =
    fetchFeatureFlags.data?.featureFlags?.[
      EFeatureFlag.DASHBOARD_DAILY_BONUS_BADGE
    ];
  const { uploadImage, getAssetUploadMessage } = useUploads();
  const [isAnimation, setIsAnimation] = useState(false);

  const validator = useMemo(() => {
    return isAnimation ? validateLottieFile : undefined;
  }, [isAnimation]);

  const buttonSuffixBadgeOptions = getImages?.data
    ? getImages.data
        .filter(
          (picture: { type: EAssetType }) =>
            picture.type === EAssetType.BUTTON_ASSET_SUFFIX
        )
        .map(
          (picture: {
            mediaId: string;
            name: string;
            url: string;
            mimeType?: string;
          }) => ({
            content: picture.name,
            key: picture.url,
            value: picture.url,
            url: picture.url,
            name: picture.name,
            mimeType: picture.mimeType
          })
        )
    : [];

  const shouldAllowTransparentBackground = useMemo(
    () =>
      generalTheme?.data?.bundlesInternalViewModel ===
      EBundlesInternalViewModel.JUNES_JOURNEY,
    [generalTheme.data?.bundlesInternalViewModel]
  );

  const isBackgroundRequired = useMemo(
    () =>
      values.offerUiType !== OfferDesignTypes.PopUp ||
      !shouldAllowTransparentBackground,
    [values.offerUiType, shouldAllowTransparentBackground]
  );

  return (
    <AcCard
      stackContainer={false}
      title="Offer Asset"
      description="Customize your store by adding brand assets that will be displayed across your web store, with the option to change them anytime."
      padded={false}
      className="offersUIFormCard"
    >
      <Grid container columnSpacing={{ xs: 1 }}>
        <Grid item xs={6}>
          <input
            style={{ display: 'none' }}
            type="file"
            onChange={(e) =>
              uploadImage(
                e,
                addPictureInputRef.current as HTMLInputElement,
                'backgroundImage',
                setFieldValue,
                setCurrentUploadingField,
                currentUploadingField
              )
            }
            ref={addPictureInputRef}
            accept="image/*"
          />
          <AcSelect
            required={isBackgroundRequired}
            header="Background Image"
            name="backgroundImage"
            value={values.backgroundImage}
            uploadConfig={{
              onUploadSuccess: async (url: string) => {
                await getImages.refetch();
                setFieldValue('backgroundImage', url);
              },
              uploadType: EAssetType.BG_BUNDLE,
              uploadMessage: getAssetUploadMessage(values.offerUiType)
            }}
            renderType={EAcSelectItemRenderType.IMAGE}
            items={[
              {
                content: 'Default Appcharge background image',
                key: 'https://media.appcharge.com/defaults/background.png',
                value: 'https://media.appcharge.com/defaults/background.png',
                url: 'https://media.appcharge.com/defaults/background.png',
                name: 'Default Appcharge background image'
              },
              ...getImages.data
                .filter((p: any) => p.type === EAssetType.BG_BUNDLE)
                .map((picture: any) => {
                  return {
                    content: picture.name,
                    key: picture.url,
                    value: picture.url,
                    url: picture.url,
                    name: picture.name
                  };
                })
            ]}
            onChange={handleChange}
            onBlur={handleBlur}
            onClear={
              values.offerUiType === OfferDesignTypes.Bundle
                ? () => {
                    setFieldValue('backgroundImage', '');
                  }
                : undefined
            }
            error={touched.backgroundImage && Boolean(errors.backgroundImage)}
            helperText={
              touched.backgroundImage || dup
                ? errors.backgroundImage?.toString()
                : ''
            }
            imgHeight={'32'}
          />
        </Grid>
        {(values.offerUiType === OfferDesignTypes.Bundle ||
          generalTheme.data?.specialOffersInternalViewModel !== 'hug') && (
          <Grid item xs={6}>
            <AcGradientInput
              header="BG Color"
              name="specialOffer.backgroundColor"
              defaultValue={values.specialOffer?.backgroundColor}
              onChange={handleChange}
              setValue={setFieldValue}
              onBlur={handleBlur}
              error={
                touched.specialOffer?.backgroundColor &&
                Boolean(errors.specialOffer?.backgroundColor)
              }
              helperText={
                touched.specialOffer?.backgroundColor
                  ? errors.specialOffer?.backgroundColor?.toString()
                  : ''
              }
            />
          </Grid>
        )}
        {values.offerUiType !== OfferDesignTypes.PopUp && (
          <Grid item xs={6}>
            <AcSelect
              imgHeight={'32'}
              header="Button Suffix Badge"
              name="buttonSuffixImage"
              value={
                values.buttonSuffixImage || values.buttonSuffixAnimation || ''
              }
              uploadConfig={{
                onUploadSuccess: async (
                  url: string,
                  name: string,
                  mimeType: string
                ) => {
                  await getImages.refetch();
                  setFieldValue('buttonSuffixFileName', name);
                  setFieldValue('buttonSuffixMimeType', mimeType);
                  const animationFlag =
                    name?.endsWith('.json') ||
                    mimeType === 'application/json' ||
                    mimeType?.includes('json');

                  if (animationFlag) {
                    setFieldValue('buttonSuffixAnimation', url);
                    setFieldValue('buttonSuffixImage', '');
                  } else {
                    setFieldValue('buttonSuffixImage', url);
                    setFieldValue('buttonSuffixAnimation', '');
                  }

                  setIsAnimation(animationFlag);
                  setButtonSuffixBadgeType(
                    animationFlag
                      ? EAcSelectItemRenderType.ANIMATION
                      : EAcSelectItemRenderType.IMAGE
                  );
                },
                uploadType: EAssetType.BUTTON_ASSET_SUFFIX,
                uploadMessage: UploadsTooltips[EAssetType.BUTTON_ASSET_SUFFIX],
                validator
              }}
              items={buttonSuffixBadgeOptions}
              renderType={EAcSelectItemRenderType.IMAGE}
              onChange={(e) => {
                const selectedItem = buttonSuffixBadgeOptions.find(
                  (item: { value: string; name: string }) =>
                    item.value === e.target.value
                );
                if (selectedItem) {
                  setFieldValue('buttonSuffixFileName', selectedItem.name);
                  setFieldValue('buttonSuffixMimeType', selectedItem.mimeType);
                  const animationFlag =
                    selectedItem.name?.endsWith('.json') ||
                    selectedItem.mimeType === 'application/json' ||
                    selectedItem.mimeType?.includes('json');

                  if (animationFlag) {
                    setFieldValue('buttonSuffixAnimation', selectedItem.value);
                    setFieldValue('buttonSuffixImage', '');
                  } else {
                    setFieldValue('buttonSuffixImage', selectedItem.value);
                    setFieldValue('buttonSuffixAnimation', '');
                  }

                  setIsAnimation(animationFlag);
                  setButtonSuffixBadgeType(
                    animationFlag
                      ? EAcSelectItemRenderType.ANIMATION
                      : EAcSelectItemRenderType.IMAGE
                  );
                }
              }}
              onBlur={handleBlur}
            />
          </Grid>
        )}
      </Grid>
      {values.offerUiType === OfferDesignTypes.PopUp &&
        hasFeatureFlagDailyBonusBadge && (
          <PopupUiForm
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            setFieldValue={setFieldValue}
            addPictureInputRef={addPictureInputRef}
            getImages={getImages}
            setCurrentUploadingField={setCurrentUploadingField}
            currentUploadingField={currentUploadingField}
            getAssetUploadMessage={getAssetUploadMessage}
            dup={dup}
          />
        )}
    </AcCard>
  );
};

export default OfferUiAssets;
